// src/components/VideoCall.jsx
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVideoCall } from '../context/VideoCallContext';

const VideoCall = () => {
  const {
    activeCall,
    incomingCall,
    isCallActive,
    localStream,
    remoteStream,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo
  } = useVideoCall();
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);
  
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);
  
  return (
    <>
      {/* Incoming Call Modal */}
      <AnimatePresence>
        {incomingCall && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full text-center">
              <div className="w-20 h-20 bg-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-phone-alt text-3xl text-orange animate-pulse"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Incoming Call</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {incomingCall.callerName} is calling you...
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => answerCall(incomingCall)}
                  className="flex-1 bg-green-500 text-white py-3 rounded-xl hover:bg-green-600 transition"
                >
                  <i className="fas fa-phone-alt mr-2"></i> Answer
                </button>
                <button
                  onClick={rejectCall}
                  className="flex-1 bg-red-500 text-white py-3 rounded-xl hover:bg-red-600 transition"
                >
                  <i className="fas fa-phone-slash mr-2"></i> Reject
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Active Call Modal */}
      <AnimatePresence>
        {activeCall && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed inset-0 bg-gray-900 z-50 flex flex-col"
          >
            {/* Remote Video (full screen) */}
            <div className="flex-1 relative bg-black">
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              
              {/* Call info overlay */}
              <div className="absolute top-4 left-4 bg-black/50 rounded-lg px-3 py-1 text-white text-sm">
                {isCallActive ? 'Call in progress' : 'Connecting...'}
              </div>
            </div>
            
            {/* Local Video (picture-in-picture) */}
            <div className="absolute top-4 right-4 w-32 h-48 md:w-48 md:h-64 rounded-xl overflow-hidden shadow-lg border-2 border-white bg-gray-800">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Call Controls */}
            <div className="bg-gray-800 p-4 flex justify-center gap-6">
              <button
                onClick={toggleMute}
                className="bg-gray-700 hover:bg-gray-600 text-white w-12 h-12 rounded-full transition"
              >
                <i className="fas fa-microphone"></i>
              </button>
              <button
                onClick={toggleVideo}
                className="bg-gray-700 hover:bg-gray-600 text-white w-12 h-12 rounded-full transition"
              >
                <i className="fas fa-video"></i>
              </button>
              <button
                onClick={endCall}
                className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-full transition"
              >
                <i className="fas fa-phone-slash"></i>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VideoCall;